import { ref, watch } from 'vue'
import { useAuthStore } from '~/stores/authStore'
import type {
  ConnectionHealth,
  HealthCheckRequest,
  SocketTopic,
  SubscriptionMessage,
  UnsubscribeMessage,
  WebSocketInstance,
  WebSocketMessageHandler
} from '~/types/socket'
import type { AllowedAny, GenericObject } from '~/types/common'
import { useMonitoring } from '~/composables/monitoring/useMonitoring'
import { useSentry } from '~/composables/monitoring/useSentry'
import type { UserActivityMessage } from '~/types/user-activity'

interface QueuedMessage {
  type: 'subscribe' | 'unsubscribe' | 'message'
  payload: GenericObject
  resolve?: () => void
  reject?: (reason?: AllowedAny) => void
}

let instance: WebSocketInstance | null = null

const MAX_RECONNECT_ATTEMPTS = 5
const INITIAL_RECONNECT_DELAY = 3000
const MAX_RECONNECT_DELAY = 30000

const createWebSocket = (): WebSocketInstance => {
  const socket = ref<WebSocket | null>(null)
  const isConnected = ref<boolean>(false)
  const isConnecting = ref<boolean>(false)
  const shouldReconnect = ref<boolean>(false)
  const reconnectAttemptsExhausted = ref<boolean>(false)
  const connectionHealth = ref<ConnectionHealth>('disconnected')
  const lastMessageTime = ref<number | null>(null)
  const latency = ref<number | null>(null)
  const uptime = ref<number>(0)
  const eventHandlers = new Map<string, Set<WebSocketMessageHandler<AllowedAny>>>()
  const messageQueue: QueuedMessage[] = []
  const activeSubscriptions = new Set<SocketTopic>()
  let reconnectDelay = INITIAL_RECONNECT_DELAY
  let reconnectTimer: NodeJS.Timeout | null = null
  let connectionPromise: Promise<void> | null = null
  let reconnectAttempts = 0
  let connectionStartTime: number | null = null
  let healthCheckInterval: NodeJS.Timeout | null = null
  let lastHealthCheckTime: number | null = null

  const authStore = useAuthStore()
  const debug = useDebug(false)
  const { trackWebsocketEvent } = useMonitoring()
  const { captureException } = useSentry()

  const buildWebSocketUrl = (): string => {
    const { accessToken, activeLicence, activeSite, sessionId } = authStore

    if (!accessToken || !activeLicence) {
      throw new Error('Missing token or licence for WebSocket connection')
    }

    const baseUrl = useRuntimeConfig().public.wsUrl
    let url = `${baseUrl}?Authorization=${accessToken}&X-Fundamentum-Licence-Id=${activeLicence.id}`
    if (sessionId) {
      url += `&X-Session-Id=${sessionId}`
    }
    if (activeSite) {
      url += `&X-Site-Id=${activeSite.id}`
    }
    return url
  }

  const resetReconnectState = (): void => {
    reconnectAttempts = 0
    reconnectDelay = INITIAL_RECONNECT_DELAY
    reconnectAttemptsExhausted.value = false
  }

  const startHealthCheckInterval = (): void => {
    if (healthCheckInterval) {
      clearInterval(healthCheckInterval)
    }

    // Send health check every 30 seconds
    healthCheckInterval = setInterval(() => {
      if (socket.value?.readyState === WebSocket.OPEN) {
        sendHealthCheck()
      }
    }, 30000)
  }

  const stopHealthCheckInterval = (): void => {
    if (healthCheckInterval) {
      clearInterval(healthCheckInterval)
      healthCheckInterval = null
    }
  }

  const sendMessage = <T = GenericObject>(data: T): void => {
    if (socket.value?.readyState === WebSocket.OPEN) {
      socket.value.send(JSON.stringify(data))
    } else {
      throw new Error('WebSocket is not connected')
    }
  }

  const sendHealthCheck = (): void => {
    if (socket.value?.readyState !== WebSocket.OPEN) return

    lastHealthCheckTime = Date.now()
    const payload: HealthCheckRequest = {
      action: 'health_check',
      timestamp: Date.now()
    }
    sendMessage(payload)
  }

  const sendUserActivity = (message: UserActivityMessage): void => {
    sendMessage({
      action: 'user_activity',
      user_activity: message
    })
  }

  const updateConnectionHealth = (): void => {
    if (!isConnected.value) {
      connectionHealth.value = 'disconnected'
      return
    }

    if (isConnecting.value) {
      connectionHealth.value = 'connecting'
      return
    }

    // Check if we have recent messages (within last 60 seconds)
    const now = Date.now()
    if (lastMessageTime.value && now - lastMessageTime.value > 60000) {
      connectionHealth.value = 'degraded'
      return
    }

    // Check latency
    if (latency.value && latency.value > 1000) {
      connectionHealth.value = 'degraded'
      return
    }

    connectionHealth.value = 'healthy'
  }

  const updateUptime = (): void => {
    if (connectionStartTime && isConnected.value) {
      uptime.value = Math.floor((Date.now() - connectionStartTime) / 1000)
    } else {
      uptime.value = 0
    }
  }

  // Update uptime every second
  setInterval(updateUptime, 1000)

  const processMessageQueue = (): void => {
    while (messageQueue.length > 0 && socket.value?.readyState === WebSocket.OPEN) {
      const queuedMessage = messageQueue.shift()
      if (!queuedMessage) continue

      try {
        if (queuedMessage.type === 'subscribe') {
          sendMessage(queuedMessage.payload)
          activeSubscriptions.add(queuedMessage.payload.topic)
        } else if (queuedMessage.type === 'unsubscribe') {
          sendMessage(queuedMessage.payload)
          activeSubscriptions.delete(queuedMessage.payload.topic)
        } else {
          sendMessage(queuedMessage.payload)
        }
        queuedMessage.resolve?.()
      } catch (error) {
        queuedMessage.reject?.(error)
      }
    }
  }

  const resubscribeAll = (): void => {
    // Re-subscribe to all active subscriptions after reconnection
    activeSubscriptions.forEach((topic: SocketTopic) => {
      const payload: SubscriptionMessage = {
        action: 'subscribe',
        topic
      }
      sendMessage(payload)
    })
  }

  const connect = (isManualConnect = false): Promise<void> => {
    if (!authStore.isAuthenticated || !authStore.hasSessionID || !authStore.activeLicence) {
      return Promise.resolve()
    }

    // If manually connecting, reset the reconnection state
    if (isManualConnect) {
      resetReconnectState()
    }

    // Check if we've exhausted reconnection attempts (only for automatic reconnects)
    if (!isManualConnect && reconnectAttemptsExhausted.value) {
      return Promise.reject(new Error('Maximum reconnection attempts exhausted'))
    }

    // Return existing connection promise if already connecting
    if (connectionPromise) {
      return connectionPromise
    }

    // Return resolved promise if already connected
    if (socket.value?.readyState === WebSocket.OPEN) {
      return Promise.resolve()
    }

    connectionPromise = new Promise((resolve, reject) => {
      try {
        if (reconnectTimer) {
          clearTimeout(reconnectTimer)
          reconnectTimer = null
        }

        // Set state
        shouldReconnect.value = true
        isConnecting.value = true

        // Close existing socket if any
        if (socket.value) {
          socket.value.onclose = null
          socket.value.close()
        }

        const wsUrl = buildWebSocketUrl()
        socket.value = new WebSocket(wsUrl)
        trackWebsocketEvent('attempting_connection', {
          attempt: String(reconnectAttempts),
          isManual: String(isManualConnect)
        })

        const connectionTimeout = setTimeout(() => {
          if (socket.value?.readyState !== WebSocket.OPEN) {
            socket.value?.close()
            connectionPromise = null
            isConnecting.value = false

            // Only increment attempts for automatic reconnections
            if (!isManualConnect) {
              reconnectAttempts++
            }

            reject(new Error('WebSocket connection timeout'))
          }
        }, 10000) // 10 seconds timeout

        socket.value.onopen = () => {
          clearTimeout(connectionTimeout)
          debug.log('WebSocket connected')
          trackWebsocketEvent('connected', {
            attempts: String(reconnectAttempts)
          })

          // Reset reconnection state on successful connection
          resetReconnectState()

          isConnected.value = true
          isConnecting.value = false
          connectionPromise = null
          connectionStartTime = Date.now()

          // Start health check interval
          startHealthCheckInterval()
          updateConnectionHealth()

          resubscribeAll()
          processMessageQueue()

          resolve()
        }

        socket.value.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data)
            const msgType = msg?.event_type ?? msg?.action
            const now = Date.now()

            // Update last message time
            lastMessageTime.value = now

            // Handle health check response for latency calculation
            if (msgType === 'health_check_response' && lastHealthCheckTime) {
              latency.value = now - lastHealthCheckTime
              lastHealthCheckTime = null
            }

            // Update connection health
            updateConnectionHealth()

            trackWebsocketEvent('message', { messageType: msgType || 'unknown' })

            const handlers = eventHandlers.get(msgType)
            if (handlers) {
              handlers.forEach((handlerFn) => {
                try {
                  handlerFn(msg)
                } catch (error) {
                  captureException(error as Error, {
                    source: 'websocket_message_handler',
                    messageType: msgType || 'unknown'
                  })
                }
              })
            }
          } catch (error) {
            captureException(error as Error, {
              source: 'websocket_message_parsing'
            })
          }
        }

        socket.value.onclose = (event) => {
          clearTimeout(connectionTimeout)
          debug.log('WebSocket disconnected', { code: event.code, reason: event.reason })
          trackWebsocketEvent('disconnected', {
            code: String(event.code),
            reason: event.reason || 'unknown'
          })

          isConnected.value = false
          isConnecting.value = false
          connectionStartTime = null

          // Stop health check interval
          stopHealthCheckInterval()
          updateConnectionHealth()

          if (connectionPromise) {
            // Connection failed during attempt
            if (!isManualConnect) {
              reconnectAttempts++
            }
            connectionPromise = null
            reject(new Error(`WebSocket closed during connection: ${event.reason || 'unknown'}`))
          }

          // Only attempt reconnection for non-manual connections
          if (shouldReconnect.value && !isManualConnect) {
            attemptReconnect()
          }
        }

        socket.value.onerror = (event) => {
          clearTimeout(connectionTimeout)
          debug.log('WebSocket error')
          trackWebsocketEvent('error', {
            readyState: socket.value?.readyState?.toString() || 'unknown',
            message: (event as ErrorEvent)?.message || 'unknown'
          })

          if (connectionPromise) {
            if (!isManualConnect) {
              reconnectAttempts++
            }
            connectionPromise = null
            reject(new Error('WebSocket error'))
          }
        }
      } catch (error) {
        isConnecting.value = false
        connectionPromise = null
        if (!isManualConnect) {
          reconnectAttempts++
        }
        reject(error)
      }
    })

    return connectionPromise
  }

  const disconnect = (): void => {
    debug.log('Disconnecting WebSocket')
    shouldReconnect.value = false

    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }

    // Reset reconnection state
    resetReconnectState()

    connectionPromise = null
    connectionStartTime = null

    // Stop health check interval
    stopHealthCheckInterval()

    activeSubscriptions.clear()

    messageQueue.forEach((msg) => msg.reject?.(new Error('WebSocket disconnected')))
    messageQueue.length = 0

    if (socket.value) {
      socket.value.onclose = null
      socket.value.onerror = null
      socket.value.onmessage = null
      socket.value.onopen = null
      socket.value.close()
      socket.value = null
    }

    isConnected.value = false
    isConnecting.value = false
    updateConnectionHealth()
  }

  const reconnect = (): Promise<void> => {
    disconnect()
    // Manual reconnect always resets the counter
    return connect(true)
  }

  const reconnectWithActiveSite = (): void => {
    if (!socket.value) {
      return
    }

    const topics = [...activeSubscriptions]
    disconnect()
    topics.forEach((topic) => activeSubscriptions.add(topic))
    connect(true).catch((error) => {
      captureException(error as Error, { source: 'websocket_site_reconnect' })
    })
  }

  watch(
    () => authStore.activeSite?.id,
    (siteId, previousSiteId) => {
      if (siteId !== previousSiteId) {
        reconnectWithActiveSite()
      }
    }
  )

  const attemptReconnect = (): void => {
    // Check if we've exceeded max attempts
    if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      debug.log(`Max reconnection attempts (${MAX_RECONNECT_ATTEMPTS}) reached. Giving up.`)
      trackWebsocketEvent('max_reconnect_attempts_reached', {
        attempts: String(reconnectAttempts)
      })
      reconnectAttemptsExhausted.value = true
      shouldReconnect.value = false

      // Clear any remaining queued messages
      messageQueue.forEach((msg) => msg.reject?.(new Error('Maximum reconnection attempts exhausted')))
      messageQueue.length = 0

      return
    }

    if (!shouldReconnect.value || reconnectTimer) {
      return
    }

    // Calculate delay with exponential backoff
    reconnectDelay = Math.min(INITIAL_RECONNECT_DELAY * Math.pow(2, reconnectAttempts), MAX_RECONNECT_DELAY)

    debug.log(
      `Reconnecting in ${reconnectDelay / 1000}s... (attempt ${reconnectAttempts + 1}/${MAX_RECONNECT_ATTEMPTS})`
    )
    trackWebsocketEvent('scheduling_reconnect', {
      delay: String(reconnectDelay),
      attempt: String(reconnectAttempts + 1),
      maxAttempts: String(MAX_RECONNECT_ATTEMPTS)
    })

    reconnectTimer = setTimeout(() => {
      reconnectTimer = null
      if (shouldReconnect.value) {
        connect(false).catch((error) => {
          trackWebsocketEvent('reconnection_attempt_failed', {
            attempt: reconnectAttempts.toString(),
            maxAttempts: MAX_RECONNECT_ATTEMPTS.toString(),
            delayMs: reconnectDelay.toString(),
            message: error instanceof Error ? error.message : String(error),
            readyState: socket.value?.readyState?.toString() || 'unknown'
          })
        })
      }
    }, reconnectDelay)
  }

  const subscribe: WebSocketInstance['subscribe'] = async (topic, ...args) => {
    if (reconnectAttemptsExhausted.value) {
      // throw new Error(
      //   'WebSocket connection failed: Maximum reconnection attempts exhausted. Please refresh the page or try again later.'
      // )
      return
    }

    const filter = args[0]
    const payload: SubscriptionMessage = {
      action: 'subscribe',
      topic,
      ...(filter ? { filter } : {})
    }

    // If connected, send immediately
    if (socket.value?.readyState === WebSocket.OPEN) {
      sendMessage(payload)
      activeSubscriptions.add(topic)
      return
    }

    // Otherwise, queue the subscription and ensure connection
    return new Promise<void>((resolve, reject) => {
      messageQueue.push({
        type: 'subscribe',
        payload,
        resolve,
        reject
      })

      connect(true).catch(reject)
    })
  }

  const unsubscribe: WebSocketInstance['unsubscribe'] = async (topic) => {
    const payload: UnsubscribeMessage = {
      action: 'unsubscribe',
      topic
    }

    activeSubscriptions.delete(topic)

    if (socket.value?.readyState === WebSocket.OPEN) {
      sendMessage(payload)
      return
    }

    // Queue the unsubscribe if not connected
    return new Promise<void>((resolve, reject) => {
      messageQueue.push({
        type: 'unsubscribe',
        payload,
        resolve,
        reject
      })
    })
  }

  const on: WebSocketInstance['on'] = (eventType, handler) => {
    if (!eventHandlers.has(eventType)) {
      eventHandlers.set(eventType, new Set())
    }
    eventHandlers.get(eventType)?.add(handler)

    // Return unsubscribe function
    return () => {
      eventHandlers.get(eventType)?.delete(handler)
    }
  }

  const off: WebSocketInstance['off'] = (eventType, handler) => {
    eventHandlers.get(eventType)?.delete(handler)
  }

  const waitForConnection = (timeout = 10000): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (socket.value?.readyState === WebSocket.OPEN) {
        resolve()
        return
      }

      const timer = setTimeout(() => {
        reject(new Error('Connection timeout'))
      }, timeout)

      const checkConnection = setInterval(() => {
        if (socket.value?.readyState === WebSocket.OPEN) {
          clearInterval(checkConnection)
          clearTimeout(timer)
          resolve()
        }
      }, 100)
    })
  }

  // TEMP: hook to simulate server-pushed events from devtools (dev + prod).
  // Usage: window.__ws.emit('session_expired', { action: 'session_expired', message: '...' })
  ;(window as unknown as { __ws: { emit: (type: string, payload: AllowedAny) => void } }).__ws = {
    emit: (type, payload) => eventHandlers.get(type)?.forEach((fn) => fn(payload))
  }

  return {
    isConnected,
    isConnecting,
    reconnectAttemptsExhausted,
    connectionHealth,
    lastMessageTime,
    latency,
    uptime,
    connect,
    disconnect,
    reconnect,
    subscribe,
    unsubscribe,
    on,
    off,
    waitForConnection,
    sendHealthCheck,
    sendUserActivity
  }
}

export const useWebSocket = (): WebSocketInstance => {
  if (!instance) {
    instance = createWebSocket()
  }
  return instance
}
